import styled from '@emotion/styled';
import { useState } from 'react';
import { logmarToSnellen, logmarToAmericanSnellen, logmarToMetric } from '../../../helpers/data';
import { EyeResultsTable, NotationSwitcher } from '../../../ui';

// Results table (Figma session/11 left): teal "Right Eye | Left Eye" headers, the logMAR score
// (">1.00" above 1), the converted acuity and a "< European Snellen >" switcher per eye.
// Conversions are the same tables components/AcuityResultsTable used (Metric = decimal acuity).

export const ACUITY_FORMATS = [
  { name: 'European Snellen', table: logmarToSnellen },
  { name: 'US Snellen', table: logmarToAmericanSnellen },
  { name: 'Decimal', table: logmarToMetric },
];
const NAMES = ACUITY_FORMATS.map((f) => f.name);

/** First row: the logMAR score, ">1.00" when above 1, else the text ("Eye not tested"). */
export const scoreLabel = (logmarSize) => (logmarSize > 1 ? '>1.00' : logmarSize || 'N/A');

/** Second row: the score converted to the chosen notation, "N/A" when there is none. */
export const convertScore = (logmarSize, formatName) => {
  if (!(Number(logmarSize) || logmarSize === '0.00')) return 'N/A';
  const format = ACUITY_FORMATS.find((f) => f.name === formatName) || ACUITY_FORMATS[0];
  return format.table[Number(logmarSize).toFixed(1)] || 'N/A';
};

const Center = styled.div`
  width: 100%;
  margin: auto 0;
  padding: 8px 0;
`;

/**
 * @param results { rightEye, leftEye, bothEyes } logMAR strings ("0.30") or "Eye not tested"
 */
export const AcuityResultsView = ({ results = {} }) => {
  const [notations, setNotations] = useState({ right: NAMES[0], left: NAMES[0], both: NAMES[0] });
  const set = (eye) => (value) => setNotations((n) => ({ ...n, [eye]: value }));
  const binocular = Boolean(results.bothEyes);
  const eyes = binocular
    ? [{ id: 'both', label: 'Binocular', value: results.bothEyes || 'Eye not tested' }]
    : [
        { id: 'right', label: 'Right Eye', value: results.rightEye || 'Eye not tested' },
        { id: 'left', label: 'Left Eye', value: results.leftEye || 'Eye not tested' },
      ];
  return (
    <Center data-testid='acuity-results'>
      <EyeResultsTable
        columns={eyes.map((e) => e.label)}
        style={binocular ? { maxWidth: 300 } : undefined}
        rows={[
          { cells: eyes.map((e) => scoreLabel(e.value)) },
          { tone: 'muted', cells: eyes.map((e) => convertScore(e.value, notations[e.id])) },
          {
            tone: 'notation',
            cells: eyes.map((e) => <NotationSwitcher key={e.id} options={NAMES} value={notations[e.id]} onChange={set(e.id)} label={`${e.label} notation`} />),
          },
        ]}
      />
    </Center>
  );
};

export default AcuityResultsView;
